import { QuestionIcon, XIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { createFormHook, createFormHookContexts, useStore } from '@tanstack/react-form'
import classNames from 'classnames'
import { useRef, useState } from 'react'
import { match } from 'ts-pattern'
import { DefaultButton } from '../../../components/buttons/default-button.tsx'
import { GhostButton } from '../../../components/buttons/ghost-button.tsx'
import * as footerStyles from '../../../components/drawers/floating-footer.css.ts'
import { FloatingFooter } from '../../../components/drawers/floating-footer.tsx'
import { RadioGroup, RadioItem } from '../../../components/input/radio.tsx'
import { SelectBox } from '../../../components/input/select-box.tsx'
import { TextInput } from '../../../components/input/text-input.tsx'
import { useShowMutationError } from '../../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../../services/toast-service.tsx'
import { usePostIntegration, type PostIntegrationVariables } from '../../../utils/api-client/api-hooks.ts'
import { DocumentationDrawer } from './documentation-drawer.tsx'
import * as styles from './integration-drawer.css.ts'
import { CatalogBrandIcon } from './integration-icon.tsx'
import type {
  CatalogEntry,
  CheckmarxRegion,
  CreatableIntegrationType,
  IntegrationField,
} from './integration-instance-utils.ts'
import { CHECKMARX_REGIONS, DEFAULT_CHECKMARX_REGION, TOOL_FIELD_CONFIG } from './integration-instance-utils.ts'

// #region Types

type WebhookAuthMethod = 'secret' | 'basic'

// The form is a flat name + a per-tool value bag + the AppScan webhook-auth method. Field state lives
// in the TanStack Form store (not in a component that renders Dialog.Content) so typing never
// re-renders Dialog.Content — that would re-run Radix's FocusScope ref callback and exceed React's
// update-depth limit.
type SetupFormValues = {
  name: string
  values: Record<string, string>
  webhookMethod: WebhookAuthMethod
}

type SetupDrawerProps = {
  entry: CatalogEntry
  onClose: () => unknown
}

// #endregion

// #region Form hook

const { fieldContext, formContext } = createFormHookContexts()

const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

const FORM_DEFAULT_VALUES: SetupFormValues = {
  name: '',
  values: {},
  webhookMethod: 'secret',
}

const useSetupForm = (defaultValueOverrides: Partial<SetupFormValues> = {}) =>
  useAppForm({ defaultValues: { ...FORM_DEFAULT_VALUES, ...defaultValueOverrides } })

const useFormContext = () => useTypedAppFormContext({ defaultValues: FORM_DEFAULT_VALUES })

// #endregion

// #region Helpers

// Seeds any field that declares a default.
const initialValuesFor = (fields: IntegrationField[]): Record<string, string> =>
  Object.fromEntries(fields.map(field => [field.key, field.defaultValue ?? '']))

// Narrows a raw select value to a CheckmarxRegion, defaulting to the first region. Submit stays gated
// until a region is chosen, so the default is only a type-level fallback.
const toCheckmarxRegion = (value: string | undefined): CheckmarxRegion =>
  CHECKMARX_REGIONS.find(region => region === value) ?? DEFAULT_CHECKMARX_REGION

const BASE_URI_KEY = 'base_uri'

/** The chooser hands back a generic `string`; this is the one place that narrows it. */
const toWebhookAuthMethod = (value: string): WebhookAuthMethod => (value === 'basic' ? 'basic' : 'secret')

const isValidBaseUri = (value: string): boolean => URL.canParse(value.trim())

/**
 * The base URI is `format: uri` in the request schema, so a value without a scheme is rejected before
 * the request leaves. Caught here as a field error the user can act on, rather than in the mutation
 * where it would surface as a parse failure with nothing naming the field.
 */
const getBaseUriError = (field: IntegrationField, value: string | undefined): string | undefined => {
  if (field.key !== BASE_URI_KEY || !value || isValidBaseUri(value)) {
    return undefined
  }
  return 'Enter a full URL, including https://'
}

// AppScan submits exactly one webhook-auth method — the platform rejects both or neither with a 400.
const buildCreateBody = (type: CreatableIntegrationType, form: SetupFormValues): PostIntegrationVariables['body'] => {
  const { webhookMethod } = form
  const name = form.name.trim()
  // A pasted key routinely carries a trailing space or newline, and sending it verbatim fails auth
  // with nothing to point at. No credential has meaningful surrounding whitespace.
  const values = Object.fromEntries(Object.entries(form.values).map(([key, value]) => [key, value.trim()]))
  return match(type)
    .with('datadog', tool => ({
      type: tool,
      name,
      base_uri: values.base_uri ?? '',
      api_key: values.api_key ?? '',
      application_key: values.application_key ?? '',
    }))
    .with('arnica', tool => ({
      type: tool,
      name,
      base_uri: values.base_uri ?? '',
      api_key: values.api_key ?? '',
    }))
    .with('sonar', tool => ({ type: tool, name, base_uri: values.base_uri ?? '', token: values.token ?? '' }))
    .with('polaris', tool => ({ type: tool, name, base_uri: values.base_uri ?? '', token: values.token ?? '' }))
    .with('veracode', tool => ({
      type: tool,
      name,
      base_uri: values.base_uri ?? '',
      api_key_id: values.api_key_id ?? '',
      api_key_secret: values.api_key_secret ?? '',
    }))
    .with('checkmarx', tool => ({
      type: tool,
      name,
      region: toCheckmarxRegion(values.region),
      tenant_account_name: values.tenant_account_name ?? '',
      api_key: values.api_key ?? '',
    }))
    .with('appscan', tool => ({
      type: tool,
      name,
      base_uri: values.base_uri ?? '',
      api_key_id: values.api_key_id ?? '',
      api_key_secret: values.api_key_secret ?? '',
      webhook_secret: webhookMethod === 'secret' ? values.webhook_secret : undefined,
      webhook_user: webhookMethod === 'basic' ? values.webhook_user : undefined,
      webhook_password: webhookMethod === 'basic' ? values.webhook_password : undefined,
    }))
    .exhaustive()
}

// #endregion

export const SetupDrawer: React.FC<SetupDrawerProps> = ({ entry, onClose }) => {
  const tool = entry.addableType
  const fields = tool ? TOOL_FIELD_CONFIG[tool] : []
  const form = useSetupForm({ values: initialValuesFor(fields) })
  const drawerContentRef = useRef<HTMLDivElement>(null)

  return (
    <Dialog.Root open modal={false} onOpenChange={open => (!open ? onClose() : undefined)}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <form.AppForm>
          <Dialog.Content
            ref={drawerContentRef}
            className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
            data-has-floating-footer={true}
            onInteractOutside={event => event.preventDefault()}
          >
            <div className={styles.header}>
              <Dialog.Title className={styles.titleRow}>
                <span className={styles.title}>Integrate with</span>
                <CatalogBrandIcon icon={entry.icon} />
                <span className={styles.title}>{entry.label}</span>
              </Dialog.Title>
              <Dialog.Close asChild>
                <button type="button" className={styles.closeButton} aria-label="Close">
                  <XIcon size={24} weight="regular" />
                </button>
              </Dialog.Close>
            </div>

            <SetupFields fields={fields} isAppScan={tool === 'appscan'} />

            <VisuallyHidden>
              <Dialog.Description>Enter your {entry.label} credentials to connect a new instance.</Dialog.Description>
            </VisuallyHidden>
          </Dialog.Content>

          <FloatingFooter drawerContentRef={drawerContentRef} width="standard">
            <SetupFooter entry={entry} onClose={onClose} />
          </FloatingFooter>
        </form.AppForm>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// #region Field leaves

type SetupFieldsProps = { fields: IntegrationField[]; isAppScan: boolean }

const SetupFields: React.FC<SetupFieldsProps> = ({ fields, isAppScan }) => {
  const form = useFormContext()
  const name = useStore(form.store, state => state.values.name)
  const values = useStore(form.store, state => state.values.values)

  const setValue = (key: string, value: string) =>
    form.setFieldValue('values', previous => ({ ...previous, [key]: value }))

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionHeading}>Connect your instance</h3>
      <div className={styles.fields}>
        {/* Required by the create contract (`minLength: 1`), and it is what tells two instances of
            the same tool apart everywhere they are listed. */}
        <TextInput
          id="integration-name"
          label="Name"
          isRequired
          value={name}
          onChange={value => form.setFieldValue('name', value)}
          placeholder="Name this integration"
          helperText="How this instance is identified across Pixee."
        />
        {fields.map(field =>
          match(field)
            .with({ kind: 'select' }, selectField => (
              <SelectBox
                key={selectField.key}
                id={`integration-${selectField.key}`}
                label={selectField.label}
                isRequired
                value={values[selectField.key]}
                onValueChange={value => setValue(selectField.key, value)}
                options={selectField.options ?? []}
              />
            ))
            .otherwise(textField => (
              <TextInput
                key={textField.key}
                id={`integration-${textField.key}`}
                label={textField.label}
                type={textField.kind === 'password' ? 'password' : 'text'}
                isRequired
                value={values[textField.key]}
                onChange={value => setValue(textField.key, value)}
                helperText={textField.helperText}
                errorText={getBaseUriError(textField, values[textField.key])}
              />
            ))
        )}
        {isAppScan && <SetupWebhookFieldset />}
      </div>
    </div>
  )
}

const SetupWebhookFieldset: React.FC = () => {
  const form = useFormContext()
  const webhookMethod = useStore(form.store, state => state.values.webhookMethod)
  const values = useStore(form.store, state => state.values.values)
  const setValue = (key: string, value: string) =>
    form.setFieldValue('values', previous => ({ ...previous, [key]: value }))

  return (
    <fieldset className={styles.webhookAuthFieldset}>
      <legend className={styles.sectionHeading}>Webhook authentication</legend>
      <RadioGroup
        className={styles.webhookAuthOptions}
        value={webhookMethod}
        onValueChange={value => form.setFieldValue('webhookMethod', toWebhookAuthMethod(value))}
      >
        <RadioItem id="webhook-method-secret" value="secret" label="Shared secret" />
        <RadioItem id="webhook-method-basic" value="basic" label="Basic auth" />
      </RadioGroup>
      {match(webhookMethod)
        .with('secret', () => (
          <TextInput
            id="integration-webhook_secret"
            label="Webhook secret"
            type="password"
            isRequired
            value={values.webhook_secret}
            onChange={value => setValue('webhook_secret', value)}
          />
        ))
        .with('basic', () => (
          <>
            <TextInput
              id="integration-webhook_user"
              label="Webhook username"
              isRequired
              value={values.webhook_user}
              onChange={value => setValue('webhook_user', value)}
            />
            <TextInput
              id="integration-webhook_password"
              label="Webhook password"
              type="password"
              isRequired
              value={values.webhook_password}
              onChange={value => setValue('webhook_password', value)}
            />
          </>
        ))
        .exhaustive()}
    </fieldset>
  )
}

// #endregion

// #region Footer leaf

type SetupFooterProps = { entry: CatalogEntry; onClose: () => unknown }

const SetupFooter: React.FC<SetupFooterProps> = ({ entry, onClose }) => {
  const form = useFormContext()
  const tool = entry.addableType
  const fields = tool ? TOOL_FIELD_CONFIG[tool] : []
  const name = useStore(form.store, state => state.values.name)
  const values = useStore(form.store, state => state.values.values)
  const webhookMethod = useStore(form.store, state => state.values.webhookMethod)

  const { addToast } = useToastService()
  const [isDocsOpen, setIsDocsOpen] = useState(false)

  const showConnectError = useShowMutationError({
    prefix: 'Failed to connect integration',
    overrides: { 409: { message: 'An integration with this name already exists.' } },
  })
  const postIntegration = usePostIntegration({
    onSuccess: () => {
      addToast({ message: <>{entry.label} integration added.</>, variant: 'success' })
      onClose()
    },
    onError: showConnectError,
  })

  const isAppScan = tool === 'appscan'
  const areFieldsFilled = fields.every(field => (values[field.key] ?? '').trim() !== '')
  // A malformed base URI fails the request schema, so hold submit rather than let the mutation throw.
  const hasValidBaseUri = fields.every(field => field.key !== BASE_URI_KEY || isValidBaseUri(values[field.key] ?? ''))
  const isWebhookFilled = match(webhookMethod)
    .with('secret', () => (values.webhook_secret ?? '').trim() !== '')
    .with('basic', () => (values.webhook_user ?? '').trim() !== '' && (values.webhook_password ?? '').trim() !== '')
    .exhaustive()
  const isSubmitDisabled =
    tool === undefined || name.trim() === '' || !areFieldsFilled || !hasValidBaseUri || (isAppScan && !isWebhookFilled)

  const handleSubmit = () => {
    if (tool === undefined) {
      return
    }
    postIntegration.mutate({ body: buildCreateBody(tool, form.state.values) })
  }

  return (
    <>
      <button type="button" className={styles.docsTrigger} onClick={() => setIsDocsOpen(prev => !prev)}>
        <span className={styles.docsTriggerIcon}>
          <QuestionIcon size={20} weight="regular" />
        </span>
        View documentation
      </button>
      <div className={styles.footerActions}>
        {/* Cancelling mid-create would leave the drawer guessing whether the instance was made, so
            the connect in flight owns the footer until it settles. */}
        <GhostButton type="secondary" state={postIntegration.isPending ? 'disabled' : 'default'} onClick={onClose}>
          Cancel
        </GhostButton>
        <DefaultButton
          buttonType="primary"
          state={postIntegration.isPending ? 'loading' : isSubmitDisabled ? 'disabled' : 'default'}
          onClick={handleSubmit}
          loadingLabel="Testing"
        >
          Test and connect
        </DefaultButton>
      </div>
      {isDocsOpen && <DocumentationDrawer entry={entry} placement="beside" onClose={() => setIsDocsOpen(false)} />}
    </>
  )
}

// #endregion
